import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Mail, Trash2, Download, RefreshCw, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LeadsPanel — Admin › Leads
 *
 * Shows every email captured by FirstVisitPromo + Newsletter section, plus
 * any manually-added leads. Supports CSV export for Mailchimp / Klaviyo / etc.
 */
export default function LeadsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`);
      setItems(data.items || []);
    } catch (err) {
      console.warn("Failed to load leads", err?.response?.status || err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const filtered = items.filter((l) =>
    !q
      ? true
      : (l.email || "").toLowerCase().includes(q.toLowerCase()) ||
        (l.name || "").toLowerCase().includes(q.toLowerCase()) ||
        (l.last_source || l.source || "").toLowerCase().includes(q.toLowerCase())
  );

  const del = async (id) => {
    if (!window.confirm("Delete this lead? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`);
      setItems((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      alert("Delete failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const exportCsv = () => {
    // Direct browser navigation — the endpoint uses cookie auth, same origin.
    window.location.href = `${API}/admin/leads.csv`;
  };

  const bySource = items.reduce((acc, l) => {
    const s = l.last_source || l.source || "other";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <section data-testid="leads-panel" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Mail size={16} className="text-[var(--mm-accent)]" />
          <h2 className="section-label">Email Leads · {items.length}</h2>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            placeholder="Filter by email / source"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="leads-search"
            className="bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none w-56"
          />
          <button
            onClick={fetchLeads}
            data-testid="leads-refresh"
            className="inline-flex items-center gap-1.5 border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-xs tracking-[0.2em] uppercase hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-colors"
            title="Reload"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={exportCsv}
            data-testid="leads-export"
            disabled={items.length === 0}
            className="inline-flex items-center gap-1.5 border border-[var(--mm-accent)] text-[var(--mm-accent)] px-3 py-2 text-xs tracking-[0.2em] uppercase hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] transition-all disabled:opacity-40"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Source breakdown pills */}
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="leads-source-breakdown">
          {Object.entries(bySource).map(([src, count]) => (
            <span
              key={src}
              className="px-3 py-1 border border-[var(--mm-line)] text-[10px] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]"
            >
              {src.replace(/_/g, " ")} · {count}
            </span>
          ))}
        </div>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--mm-line)] p-10 text-center text-[var(--mm-cream-muted)]">
          <UserPlus className="mx-auto mb-3 text-[var(--mm-accent)]" size={22} />
          <p className="font-display italic text-xl text-[var(--mm-cream)] mb-1">No leads yet</p>
          <p className="text-xs">The first-visit popup and newsletter section feed this list automatically.</p>
        </div>
      ) : (
        <div className="border border-[var(--mm-line)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--mm-bg-alt)]">
              <tr className="text-left text-[10px] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Submitted</th>
                <th className="px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr
                  key={l.id}
                  data-testid={`lead-row-${l.id}`}
                  className="border-t border-[var(--mm-line)] hover:bg-[var(--mm-bg-alt)]/40"
                >
                  <td className="px-3 py-2 text-[var(--mm-cream)]">
                    {l.email}
                    {l.welcome_email_sent_at && (
                      <span
                        className="ml-2 text-[9px] tracking-[0.2em] uppercase text-emerald-400"
                        title={`Welcome email sent ${new Date(l.welcome_email_sent_at).toLocaleString("en-AU")}`}
                      >
                        · Welcome ✓
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[var(--mm-cream-muted)]">{l.name || "—"}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--mm-accent)]">
                      {(l.last_source || l.source || "other").replace(/_/g, " ")}
                    </span>
                    {l.submissions > 1 && (
                      <span className="ml-2 text-[10px] text-[var(--mm-cream-muted)]">×{l.submissions}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[var(--mm-cream-muted)] text-xs">
                    {new Date(l.created_at).toLocaleDateString("en-AU")}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => del(l.id)}
                      data-testid={`lead-delete-${l.id}`}
                      className="text-[var(--mm-cream-muted)] hover:text-red-400 transition-colors"
                      title="Delete lead"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-[var(--mm-cream-muted)] text-xs italic">
                    No leads match &quot;{q}&quot;
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
